/**
 * Provider registry — single source of truth for all integrations.
 * Adding a new integration in future = add an entry here. Zero backend changes.
 *
 * Field semantics
 * ───────────────
 *   id              — kebab-case key, matches backend PROVIDER_SPECS for OAuth
 *   name            — display label
 *   logo            — path under /public/ (placeholder svgs in /public/logos)
 *   category        — one of: outreach | ads | messaging | enrichment | scheduling | productivity | payments | email | social
 *   services        — friendly list of capabilities unlocked by this integration
 *   oauth           — true if OAuth provider (uses /configure + /auth-url + /callback)
 *   api_key         — true if simple API-key provider (uses /configure-api-key + /test)
 *   requires        — list of fields the user must paste in the Connect modal
 *   scopes          — OAuth scope strings (display + caller-side narrowing)
 *   setup_instructions
 *   console_url     — link the user opens to find their CLIENT_ID / API key
 *   callback_path   — informational; backend uses /api/integrations/{id}/callback
 *   pull_label      — label shown on the Command Center "Pull from X" button
 *   supports_pull   — whether ARIA can sync inbound leads from this provider
 *   what_this_unlocks — 3 short bullets shown in the Connect modal
 */
export const INTEGRATIONS = [
  {
    id: 'google',
    name: 'Google',
    logo: '/logos/google.svg',
    category: 'productivity',
    services: ['Gmail', 'Google Calendar', 'Google Meet', 'Google Ads'],
    oauth: true,
    scopes: [
      'https://www.googleapis.com/auth/gmail.readonly',
      'https://www.googleapis.com/auth/gmail.send',
      'https://www.googleapis.com/auth/calendar.events',
      'https://www.googleapis.com/auth/calendar.readonly',
      'https://www.googleapis.com/auth/adwords',
    ],
    setup_instructions: 'Create an OAuth app at console.cloud.google.com → APIs & Services → Credentials.',
    console_url: 'https://console.cloud.google.com/apis/credentials',
    requires: ['client_id', 'client_secret'],
    callback_path: '/api/integrations/google/callback',
    pull_label: 'Pull from Gmail',
    supports_pull: true,
    what_this_unlocks: [
      'Auto-book calls on your Google Calendar with Meet links generated',
      'Reply tracking on outbound email threads from Gmail',
      'Pull leads from Google Ads Lead Forms every 15 min',
    ],
  },
  {
    id: 'microsoft',
    name: 'Microsoft',
    logo: '/logos/microsoft.svg',
    category: 'productivity',
    services: ['Outlook', 'Teams'],
    oauth: true,
    scopes: ['Mail.Read', 'Mail.Send', 'Calendars.ReadWrite'],
    setup_instructions: 'Create an app at portal.azure.com → App registrations → New registration.',
    console_url: 'https://portal.azure.com/#blade/Microsoft_AAD_RegisteredApps/ApplicationsListBlade',
    requires: ['client_id', 'client_secret'],
    callback_path: '/api/integrations/microsoft/callback',
    pull_label: 'Pull from Outlook',
    supports_pull: true,
    what_this_unlocks: [
      'Read & send email replies from Outlook',
      'Auto-book calls on your Outlook Calendar',
      'Teams meeting link generation on booking',
    ],
  },
  {
    id: 'meta',
    name: 'Meta',
    logo: '/logos/meta.svg',
    category: 'ads',
    services: ['Facebook Ads', 'Instagram Ads', 'Lead Gen Forms'],
    oauth: true,
    scopes: ['ads_read', 'leads_retrieval', 'pages_read_engagement'],
    setup_instructions: 'Create an app at developers.facebook.com → My Apps → Create App → Business.',
    console_url: 'https://developers.facebook.com/apps',
    requires: ['client_id', 'client_secret'],
    callback_path: '/api/integrations/meta/callback',
    pull_label: 'Pull from Meta Ads',
    supports_pull: true,
    what_this_unlocks: [
      'Pull leads from Facebook + Instagram Lead Gen Forms in real time',
      'Read ad performance for ICP attribution',
      'Engagement data from your Pages',
    ],
  },
  {
    id: 'linkedin',
    name: 'LinkedIn',
    logo: '/logos/linkedin.svg',
    category: 'social',
    services: ['LinkedIn Ads', 'LinkedIn Organic'],
    oauth: true,
    scopes: ['r_liteprofile', 'r_emailaddress', 'w_member_social'],
    setup_instructions: 'Create an app at linkedin.com/developers → Create App.',
    console_url: 'https://www.linkedin.com/developers/apps',
    requires: ['client_id', 'client_secret'],
    callback_path: '/api/integrations/linkedin/callback',
    pull_label: 'Pull from LinkedIn',
    supports_pull: true,
    what_this_unlocks: [
      'Organic post scheduling on LinkedIn',
      'Profile + engagement data for prospect intel',
      'Lead Gen Form submissions',
    ],
  },
  {
    id: 'calendly',
    name: 'Calendly',
    logo: '/logos/calendly.svg',
    category: 'scheduling',
    services: ['Auto-webhook booking', 'No-show recovery'],
    oauth: true,
    scopes: ['default'],
    setup_instructions: 'Create an OAuth app at calendly.com/integrations/api_webhooks → OAuth Apps.',
    console_url: 'https://calendly.com/integrations/api_webhooks',
    requires: ['client_id', 'client_secret'],
    callback_path: '/api/integrations/calendly/callback',
    pull_label: null,
    supports_pull: false,
    what_this_unlocks: [
      'Auto-register webhooks: booked / cancelled / no-show',
      'ARIA books calls on your existing Calendly link',
      'No-show recovery — automatic reschedule outreach',
    ],
  },
  {
    id: 'saleshandy',
    name: 'Saleshandy',
    logo: '/logos/saleshandy.svg',
    category: 'outreach',
    services: ['Cold email sequences'],
    oauth: false,
    api_key: true,
    requires: ['api_key'],
    setup_instructions: 'Find your API key at Saleshandy → Settings → API.',
    console_url: 'https://app.saleshandy.com/settings/api',
    pull_label: 'Pull from Saleshandy',
    supports_pull: true,
    what_this_unlocks: [
      'Pull contacts + reply events from your sequences',
      'Pause sequences automatically when ARIA takes over',
      'Sync open / click / reply tracking',
    ],
  },
  {
    id: 'lemlist',
    name: 'Lemlist',
    logo: '/logos/lemlist.svg',
    category: 'outreach',
    services: ['Multi-channel sequences'],
    oauth: false,
    api_key: true,
    requires: ['api_key'],
    setup_instructions: 'Find your API key at Lemlist → Settings → Integrations → API.',
    console_url: 'https://app.lemlist.com/settings',
    pull_label: 'Pull from Lemlist',
    supports_pull: true,
    what_this_unlocks: [
      'Pull contacts + reply events from your campaigns',
      'LinkedIn connection-accepted webhooks',
      'Pause campaigns when ARIA takes over',
    ],
  },
  {
    id: '360dialog',
    name: '360dialog',
    logo: '/logos/360dialog.svg',
    category: 'messaging',
    services: ['WhatsApp Business API'],
    oauth: false,
    api_key: true,
    requires: ['api_key', 'phone_number_id'],
    setup_instructions: 'Find your API key + Phone Number ID at hub.360dialog.com → Channels.',
    console_url: 'https://hub.360dialog.com/dashboard',
    pull_label: null,
    supports_pull: false,
    what_this_unlocks: [
      'ARIA sends + receives WhatsApp messages',
      'Template approval workflow',
      'Inbound replies routed to Conversations',
    ],
  },
  {
    id: 'resend',
    name: 'Resend',
    logo: '/logos/resend.svg',
    category: 'email',
    services: ['Transactional email'],
    oauth: false,
    api_key: true,
    requires: ['api_key'],
    setup_instructions: 'Find your API key at resend.com → API Keys.',
    console_url: 'https://resend.com/api-keys',
    pull_label: null,
    supports_pull: false,
    what_this_unlocks: [
      'ARIA sends transactional + sequence emails reliably',
      'Webhook delivery + open + bounce tracking',
      'High-deliverability sender reputation',
    ],
  },
  {
    id: 'rapidapi',
    name: 'RapidAPI · LinkedIn',
    logo: '/logos/rapidapi.svg',
    category: 'enrichment',
    services: ['LinkedIn profile + posts + company via RapidAPI'],
    oauth: false,
    api_key: true,
    requires: ['api_key', 'host'],
    setup_instructions:
      'Subscribe to a LinkedIn scraper on rapidapi.com. Default host is ' +
      'linkedin-data-api.p.rapidapi.com (Rocketrider). Copy the X-RapidAPI-Key from any ' +
      'code sample, paste here, leave Host empty for the default or override with ' +
      'fresh-linkedin-profile-data.p.rapidapi.com.',
    console_url: 'https://rapidapi.com/rockapis-rockapis-default/api/linkedin-data-api',
    pull_label: null,
    supports_pull: false,
    what_this_unlocks: [
      'LinkedIn profile + company data (replaces Proxycurl)',
      'Recent posts feed — catch deal announcements & job changes',
      'Pay-per-call pricing via your own RapidAPI subscription',
    ],
  },
  {
    id: 'proxycurl',
    name: 'Proxycurl',
    logo: '/logos/proxycurl.svg',
    category: 'enrichment',
    services: ['LinkedIn data enrichment (legacy)'],
    oauth: false,
    api_key: true,
    requires: ['api_key'],
    setup_instructions: 'Find your API key at nubela.co/proxycurl → Dashboard. (Legacy — RapidAPI is now the default LinkedIn provider.)',
    console_url: 'https://nubela.co/proxycurl/dashboard',
    pull_label: null,
    supports_pull: false,
    what_this_unlocks: [
      'LinkedIn profile + company enrichment',
      'Posts, activity, job-change signals',
      'Powers the B2B Instinct Engine',
    ],
  },
  {
    id: 'serper',
    name: 'Serper',
    logo: '/logos/serper.svg',
    category: 'enrichment',
    services: ['Google Search API'],
    oauth: false,
    api_key: true,
    requires: ['api_key'],
    setup_instructions: 'Find your API key at serper.dev → Dashboard.',
    console_url: 'https://serper.dev/api-key',
    pull_label: null,
    supports_pull: false,
    what_this_unlocks: [
      'Google News + web search for prospect intel',
      'Real-time press / funding announcements',
      'Backs B2B signal classification',
    ],
  },
  {
    id: 'apollo',
    name: 'Apollo',
    logo: '/logos/apollo.svg',
    category: 'enrichment',
    services: ['Contact database'],
    oauth: false,
    api_key: true,
    requires: ['api_key'],
    setup_instructions: 'Find your API key at app.apollo.io → Settings → Integrations.',
    console_url: 'https://app.apollo.io/#/settings/integrations',
    pull_label: null,
    supports_pull: false,
    what_this_unlocks: [
      'Email + phone enrichment',
      'Bulk import qualified prospects',
      'ICP filtering on the Apollo DB',
    ],
  },
  {
    id: 'stripe',
    name: 'Stripe',
    logo: '/logos/stripe.svg',
    category: 'payments',
    services: ['Subscription billing'],
    oauth: false,
    api_key: true,
    requires: ['api_key', 'webhook_secret'],
    setup_instructions: 'Get your API key + webhook secret at dashboard.stripe.com → Developers.',
    console_url: 'https://dashboard.stripe.com/apikeys',
    pull_label: null,
    supports_pull: false,
    what_this_unlocks: [
      'Subscription billing on workspaces',
      'Webhook events for plan changes',
      'Customer portal',
    ],
  },
];

export const CATEGORIES = [
  { id: 'all', label: 'All' },
  { id: 'outreach', label: 'Outreach' },
  { id: 'ads', label: 'Ads' },
  { id: 'messaging', label: 'Messaging' },
  { id: 'enrichment', label: 'Enrichment' },
  { id: 'scheduling', label: 'Scheduling' },
  { id: 'productivity', label: 'Productivity' },
  { id: 'social', label: 'Social' },
  { id: 'email', label: 'Email' },
  { id: 'payments', label: 'Payments' },
];

export const getIntegration = (id) => INTEGRATIONS.find((i) => i.id === id);
